import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, ClipboardList, ToggleLeft, ToggleRight, Copy, QrCode, Trash2, BarChart3, AlertTriangle, Star } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select';

import { surveysAPI } from '../api';
import { organizationsAPI } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { SurveyHelpButton } from '../components/SurveyHelpButton';
import TranslationHealthStrip from '../components/TranslationHealthStrip';
import TranslationStatusPill from '../components/TranslationStatusPill';
import SurveyWizard from '../components/SurveyWizard';
import OrgLocationPicker from '../components/OrgLocationPicker';
import GuidedSurveyWizard from '../guided/GuidedSurveyWizard';
import NewSurveyChooserModal from '../guided/NewSurveyChooserModal';
import InsightCampaignsLauncher from '../campaigns/InsightCampaignsLauncher';


const SurveysList = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === 'super_admin';
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [preflightOpen, setPreflightOpen] = useState(false);
    // P_GUIDED_SURVEY_EXPERIENCES_1.0 — chooser + guided wizard state.
    const [chooserOpen, setChooserOpen] = useState(false);
    const [guidedOpen, setGuidedOpen] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [qrSurvey, setQrSurvey] = useState(null);
    const [duplicateTarget, setDuplicateTarget] = useState(null);
    const [duplicating, setDuplicating] = useState(false);
    // v3.5.8 — Default canManage to true for Super Admin so they're never
    // accidentally locked out by a transient /_status fetch failure.
    const [canManage, setCanManage] = useState(isSuperAdmin);

    const lang = i18n.language || 'en';

    const load = async () => {
        try {
            const data = await surveysAPI.list();
            setSurveys(data.surveys || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('surveys.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        surveysAPI.listTemplates()
            .then(d => setTemplates(d.templates || []))
            .catch(() => {});
        surveysAPI.status()
            .then(d => setCanManage(!!d.can_manage))
            .catch(() => setCanManage(isSuperAdmin));
    }, [isSuperAdmin]);

    // v3.5.14 — Auto-refresh while any survey translation is in progress.
    // Polls every 4s; stops as soon as nothing is pending. Lets the
    // TranslationStatusPill bars move from red → orange → green in real time
    // without a manual page reload.
    useEffect(() => {
        const anyPending = (surveys || []).some(
            (s) => (s.translation_meta?.status || 'success') === 'pending'
        );
        if (!anyPending) return undefined;
        const handle = setInterval(() => { load(); }, 4000);
        return () => clearInterval(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surveys]);


    const publicUrl = (slug) => `${window.location.origin}/s/${slug}`;

    const handleToggle = async (survey) => {
        try {
            await surveysAPI.update(survey.id, { enabled: !survey.enabled });
            toast.success(survey.enabled ? t('surveys.disabled') : t('surveys.enabled'));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('surveys.toggleFailed'));
        }
    };

    // P_SURVEY_MODULE_GATE_PARITY_1.0 — super-admin shortcut to flip
    // the org-level Surveys module on. Without this, the only way to
    // unblock a survey whose org-gate is OFF is to dig into Org admin.
    const handleEnableModule = async (survey) => {
        try {
            await organizationsAPI.toggleSurveysModule(survey.organization_id, true);
            toast.success(t('surveys.moduleEnabledForOrg', 'Surveys module enabled for this organization'));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('surveys.moduleEnableFailed', 'Could not enable the module'));
        }
    };

    const handleDelete = async (survey) => {
        if (!window.confirm(t('surveys.confirmDelete', { name: survey.name?.[lang] || survey.slug }))) return;
        try {
            await surveysAPI.remove(survey.id);
            toast.success(t('surveys.deleted'));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('surveys.deleteFailed'));
        }
    };

    const handleCopyUrl = (slug) => {
        navigator.clipboard.writeText(publicUrl(slug));
        toast.success(t('surveys.urlCopied'));
    };

    const handleDuplicate = async () => {
        if (!duplicateTarget) return;
        setDuplicating(true);
        try {
            const created = await surveysAPI.duplicate(duplicateTarget.id);
            toast.success(t('surveys.duplicated', 'Survey duplicated'));
            setDuplicateTarget(null);
            navigate(`/surveys/${created.id}`); // open the new copy in edit mode
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('surveys.duplicateFailed', 'Duplicate failed'));
        } finally {
            setDuplicating(false);
        }
    };

    return (
        <div className="p-4 sm:p-6 space-y-6" data-testid="surveys-list-page">
            <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3">
                <div>
                    <h1
                        className="text-2xl sm:text-3xl font-bold flex items-center gap-2"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                        <ClipboardList className="w-6 h-6 text-fuchsia-600" />
                        {t('surveys.title')}
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('surveys.subtitle')}</p>
                </div>
                <div className="flex flex-col sm:items-end gap-2">
                    {canManage && <TranslationHealthStrip />}
                    <Button
                        onClick={() => {
                            // P_GUIDED_SURVEY_EXPERIENCES_1.0 — outcome-first
                            // chooser is now the default landing point.
                            // Authors who previously opted to skip the
                            // setup helper still see the chooser; they can
                            // pick "Blank Survey" and we'll honour the
                            // remembered preference by jumping straight to
                            // the simple create form.
                            setChooserOpen(true);
                        }}
                        data-testid="surveys-create-btn"
                        data-insight-id="surveys.action.new-survey"
                        data-insight-label="New Survey"
                        disabled={!canManage}
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('surveys.newSurvey')}
                    </Button>
                </div>
            </div>

            {!canManage && !loading && (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-900 dark:text-amber-200" data-testid="surveys-readonly-banner">
                    {t('surveys.readOnlyBanner')}
                </div>
            )}

            <div className="flex justify-end -mt-3">
                <SurveyHelpButton onStartGuidedSetup={() => setWizardOpen(true)} />
            </div>

            {/* P_SURVEY_INTELLIGENCE_ENGINE_1.0 — Slice A. Insight
                Campaigns launcher. Outcome-first business initiatives
                that each launch the existing Guided Survey engine. */}
            {canManage && !loading && <InsightCampaignsLauncher />}

            {loading && <p className="text-muted-foreground">{t('common.loading')}</p>}

            {!loading && surveys.length === 0 && (
                <Card>
                    <CardContent className="py-10 text-center space-y-3">
                        <ClipboardList className="w-10 h-10 text-muted-foreground mx-auto" />
                        <p className="font-medium">{t('surveys.emptyTitle')}</p>
                        <p className="text-sm text-muted-foreground">{t('surveys.emptyDesc')}</p>
                        <Button onClick={() => setCreateOpen(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            {t('surveys.createFirst')}
                        </Button>
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {surveys.map(s => {
                    // P_SURVEY_MODULE_GATE_PARITY_1.0 — derive the
                    // publicly-reachable state for this card. A survey
                    // is reachable only when BOTH the survey itself is
                    // `enabled` AND its parent org has the Surveys
                    // module turned on. The public endpoint enforces
                    // the same logic — we mirror it in the UI so we
                    // never lie to admins about availability.
                    const moduleOn = s.org_surveys_module_enabled !== false;
                    const publiclyReachable = s.enabled && moduleOn;
                    return (
                    <Card
                        key={s.id}
                        className="hover:shadow-md transition-shadow"
                        data-testid={`survey-card-${s.id}`}
                        data-insight-id="surveys.card"
                        data-insight-label="Survey card"
                    >
                        <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                                <CardTitle className="text-base truncate">
                                    {s.name?.[lang] || s.name?.en || s.slug}
                                </CardTitle>
                                {!moduleOn ? (
                                    <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" data-testid={`module-disabled-badge-${s.id}`}>
                                        {t('surveys.moduleDisabledBadge', 'Module disabled')}
                                    </Badge>
                                ) : publiclyReachable ? (
                                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{t('surveys.statusActive')}</Badge>
                                ) : (
                                    <Badge variant="secondary">{t('surveys.statusInactive')}</Badge>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground font-mono mt-1">/s/{s.slug}</p>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            {!moduleOn && (
                                <div
                                    className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-2.5 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-2"
                                    data-testid={`module-disabled-warning-${s.id}`}
                                >
                                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <div className="space-y-1.5">
                                        <p>
                                            {t(
                                                'surveys.moduleDisabledWarning',
                                                'Public link is not reachable — the Surveys module is disabled for this organization.'
                                            )}
                                        </p>
                                        {isSuperAdmin && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleEnableModule(s)}
                                                className="h-7 text-xs border-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                                                data-testid={`enable-module-${s.id}`}
                                            >
                                                {t('surveys.enableModuleButton', 'Enable Surveys Module')}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">{t('surveys.responses')}</span>
                                <span className="font-semibold">{s.response_count ?? 0}</span>
                            </div>
                            <TranslationStatusPill meta={s.translation_meta} />
                            {/* P_SURVEY_EDITOR_UX_ENHANCEMENT_1.1 — compact
                                metadata chip row. Only renders the chips that
                                are relevant for this survey so cards never
                                grow taller than needed. */}
                            {(() => {
                                const qs = s.questions || [];
                                const qCount = qs.filter((q) => q.enabled !== false).length;
                                const hasBranching = qs.some((q) => q.response_type === 'multiple_choice_branching');
                                const oeOn = s.overall_experience_enabled !== false;
                                if (!oeOn && !hasBranching && qCount === 0) return null;
                                return (
                                    <div
                                        className="flex flex-wrap items-center gap-1.5 text-[11px]"
                                        data-testid={`survey-meta-chips-${s.id}`}
                                    >
                                        {oeOn && (
                                            <Badge
                                                variant="secondary"
                                                className="bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200 font-normal"
                                                data-testid={`oe-badge-${s.id}`}
                                                title={t('surveys.overallExperience.cardBadgeTooltip', 'This survey starts with the built-in Overall Experience 5-star screen.')}
                                            >
                                                <Star className="w-3 h-3 mr-1" />
                                                {t('surveys.overallExperience.cardBadge', 'Overall Experience')}
                                            </Badge>
                                        )}
                                        {hasBranching && (
                                            <Badge
                                                variant="secondary"
                                                className="bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-950/40 dark:text-fuchsia-200 font-normal"
                                                data-testid={`branching-badge-${s.id}`}
                                            >
                                                {t('surveys.cardMeta.branching', 'Branching')}
                                            </Badge>
                                        )}
                                        {qCount > 0 && (
                                            <Badge
                                                variant="secondary"
                                                className="font-normal"
                                                data-testid={`q-count-badge-${s.id}`}
                                            >
                                                {t('surveys.cardMeta.questions', '{{count}} Questions', { count: qCount })}
                                            </Badge>
                                        )}
                                    </div>
                                );
                            })()}
                            <div className="flex flex-wrap gap-2 pt-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleCopyUrl(s.slug)}
                                    disabled={!publiclyReachable}
                                    data-testid={`copy-url-${s.id}`}
                                    data-insight-id="surveys.action.copy-url"
                                    data-insight-label="Copy survey URL"
                                    title={!publiclyReachable ? t('surveys.moduleDisabledActionHint', 'Public link is not active') : undefined}
                                >
                                    <Copy className="w-3.5 h-3.5 mr-1.5" />
                                    {t('common.copyUrl')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setQrSurvey(s)}
                                    disabled={!publiclyReachable}
                                    data-testid={`qr-${s.id}`}
                                    data-insight-id="surveys.action.qr"
                                    data-insight-label="Survey QR code"
                                    title={!publiclyReachable ? t('surveys.moduleDisabledActionHint', 'Public link is not active') : undefined}
                                >
                                    <QrCode className="w-3.5 h-3.5 mr-1.5" />
                                    QR
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => navigate(`/surveys/${s.id}`)} data-testid={`edit-${s.id}`}>
                                    {canManage ? t('common.edit') : t('common.view')}
                                </Button>
                                {canManage && (
                                    <Button size="sm" variant="outline" onClick={() => setDuplicateTarget(s)} data-testid={`duplicate-${s.id}`}>
                                        <Copy className="w-3.5 h-3.5 mr-1.5" />
                                        {t('surveys.duplicate', 'Duplicate')}
                                    </Button>
                                )}
                                <Button
                                    size="sm"
                                    variant={s.response_count > 0 ? 'default' : 'outline'}
                                    onClick={() => navigate(`/surveys/${s.id}/responses`)}
                                    data-testid={`responses-${s.id}`}
                                    data-insight-id="surveys.action.view-results"
                                    data-insight-label="View results"
                                    className={s.response_count > 0 ? 'bg-fuchsia-600 hover:bg-fuchsia-700 text-white' : ''}
                                >
                                    <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
                                    {t('surveys.results.viewResults', 'View results')}
                                    {s.response_count > 0 && (
                                        <Badge variant="secondary" className="ml-1.5 bg-white/20 text-white border-white/30 text-[10px] px-1.5 h-4">
                                            {s.response_count}
                                        </Badge>
                                    )}
                                </Button>
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={s.enabled}
                                        onCheckedChange={() => handleToggle(s)}
                                        data-testid={`toggle-${s.id}`}
                                        data-insight-id="surveys.action.toggle-enabled"
                                        data-insight-label="Enable / disable survey"
                                        disabled={!canManage}
                                    />
                                    <span className="text-xs text-muted-foreground">
                                        {s.enabled ? t('surveys.enabled') : t('surveys.disabled')}
                                    </span>
                                </div>
                                {canManage && (
                                    <Button size="sm" variant="ghost" className="text-red-500 dark:text-red-400" onClick={() => handleDelete(s)} data-testid={`delete-${s.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                    );
                })}
            </div>

            <CreateSurveyDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                templates={templates}
                onCreated={() => { setCreateOpen(false); load(); }}
            />

            <SurveyWizard
                open={wizardOpen}
                onOpenChange={setWizardOpen}
                onCreated={() => load()}
            />

            {/* P_GUIDED_SURVEY_EXPERIENCES_1.0 — Outcome-first chooser
                and wizard. Mounted next to the existing creation modals
                so the single-engine principle holds: every path here
                ends up creating a Survey via the existing Builder API. */}
            <NewSurveyChooserModal
                open={chooserOpen}
                onOpenChange={setChooserOpen}
                onGuided={() => { setChooserOpen(false); setGuidedOpen(true); }}
                onBlank={() => {
                    setChooserOpen(false);
                    const skip = localStorage.getItem('surveys_wizard_skipped') === '1';
                    if (skip) {
                        setCreateOpen(true);
                    } else {
                        setPreflightOpen(true);
                    }
                }}
            />
            <GuidedSurveyWizard
                open={guidedOpen}
                onOpenChange={setGuidedOpen}
                onCreated={() => load()}
            />

            <PreflightDialog
                open={preflightOpen}
                onOpenChange={setPreflightOpen}
                onGuided={() => { setPreflightOpen(false); setWizardOpen(true); }}
                onSkip={(remember) => {
                    if (remember) localStorage.setItem('surveys_wizard_skipped', '1');
                    setPreflightOpen(false);
                    setCreateOpen(true);
                }}
            />

            <QrDialog survey={qrSurvey} url={qrSurvey ? publicUrl(qrSurvey.slug) : ''} onClose={() => setQrSurvey(null)} />

            <Dialog open={!!duplicateTarget} onOpenChange={(o) => { if (!o) setDuplicateTarget(null); }}>
                <DialogContent data-testid="duplicate-survey-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('surveys.duplicateTitle', 'Duplicate Survey')}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        {t('surveys.duplicateDesc', 'This will create a new independent copy of this survey with all of its current questions, logic, configuration and presentation settings. You can change anything you want after duplication.')}
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDuplicateTarget(null)} disabled={duplicating} data-testid="duplicate-cancel-btn">
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button onClick={handleDuplicate} disabled={duplicating} data-testid="duplicate-confirm-btn">
                            {duplicating ? t('surveys.duplicating', 'Duplicating…') : t('surveys.duplicate', 'Duplicate')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const CreateSurveyDialog = ({ open, onOpenChange, templates, onCreated }) => {
    const { t, i18n } = useTranslation();
    const [template, setTemplate] = useState('restroom');
    const [primaryName, setPrimaryName] = useState('');
    // Portal-owned context: organization → location. The URL is auto-generated.
    const [organizationId, setOrganizationId] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [pickerMeta, setPickerMeta] = useState({ loaded: false, orgCount: 0, locationCount: 0 });
    const [saving, setSaving] = useState(false);

    const lang = i18n.language || 'en';

    const reset = () => {
        setTemplate('restroom');
        setPrimaryName('');
        setOrganizationId('');
        setSelectedLocation('');
    };

    const needsLocationPrompt = pickerMeta.locationCount > 1 && !selectedLocation;

    const handleCreate = async () => {
        if (!primaryName.trim()) {
            toast.error(t('surveys.requiredFields'));
            return;
        }
        if (!organizationId) {
            toast.error(t('surveys.selectOrganizationFirst', 'Please select an organization first.'));
            return;
        }
        if (needsLocationPrompt) {
            toast.error(t('surveys.selectLocationFirst', 'Please select a location before publishing.'));
            return;
        }
        setSaving(true);
        try {
            // Admin types in UI language only; auto-translation fills other locales post-save.
            const nameObj = { en: '', fr: '', es: '' };
            nameObj[lang] = primaryName;
            const payload = {
                template,
                name: nameObj,
                source_language: lang,
                organization_id: organizationId,
            };
            if (selectedLocation) payload.location_id = selectedLocation;
            await surveysAPI.create(payload);
            toast.success(t('surveys.created'));
            reset();
            onCreated?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('surveys.createFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('surveys.newSurvey')}
                    </DialogTitle>
                </DialogHeader>
                <p className="text-xs text-muted-foreground">{t('common.requiredFields')}</p>
                <div className="space-y-4 py-2">
                    <div>
                        <Label className="mb-2 block">{t('surveys.template')} <span className="text-red-500">*</span></Label>
                        <Select value={template} onValueChange={setTemplate}>
                            <SelectTrigger data-testid="survey-template-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {templates.map(t2 => (
                                    <SelectItem key={t2.template_id} value={t2.template_id}>
                                        {t2.display_name?.[lang] || t2.display_name?.en || t2.template_id}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <OrgLocationPicker
                        organizationId={organizationId}
                        locationId={selectedLocation}
                        onChange={({ organizationId: o, locationId: l }) => {
                            setOrganizationId(o);
                            setSelectedLocation(l || '');
                        }}
                        onMeta={setPickerMeta}
                        testIdPrefix="survey"
                    />
                    <div>
                        <Label className="mb-2 block">{t('surveys.nameLabel', 'Survey name')} <span className="text-red-500">*</span></Label>
                        <Input
                            value={primaryName}
                            onChange={(e) => setPrimaryName(e.target.value)}
                            data-testid="survey-name-input"
                            placeholder={t('surveys.namePlaceholder', 'e.g. Bathroom Feedback')}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('surveys.autoTranslateHint', 'Other languages will be translated automatically after save.')}
                        </p>
                    </div>

                    <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-3 py-2.5" data-testid="create-url-managed-note">
                        <p className="text-xs text-muted-foreground">
                            {t('surveys.urlManagedHelp', 'Your public survey link is generated and managed automatically from your organization, location and survey name — you never have to create or edit a URL.')}
                        </p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>{t('common.cancel')}</Button>
                    <Button onClick={handleCreate} disabled={saving} data-testid="survey-create-confirm-btn">
                        {saving ? t('common.saving') : t('common.create')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const QrDialog = ({ survey, url, onClose }) => {
    const { t } = useTranslation();
    if (!survey) return null;
    const handleCopy = () => {
        navigator.clipboard.writeText(url);
        toast.success(t('surveys.urlCopied'));
    };
    return (
        <Dialog open={!!survey} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle>{t('surveys.qrTitle')}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4 py-4">
                    <div className="bg-white p-4 rounded-md border">
                        <QRCodeSVG value={url} size={220} />
                    </div>
                    <p className="text-xs text-muted-foreground break-all text-center font-mono">{url}</p>
                    <Button onClick={handleCopy} variant="outline" size="sm" data-testid="qr-copy-url-btn">
                        <Copy className="w-3.5 h-3.5 mr-1.5" />
                        {t('common.copyUrl')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const PreflightDialog = ({ open, onOpenChange, onGuided, onSkip }) => {
    const { t } = useTranslation();
    const [remember, setRemember] = useState(false);
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md" data-testid="surveys-blank-preflight">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('surveys.preflightTitle')}
                    </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground py-2">
                    {t('surveys.preflightDesc')}
                </p>
                <div className="flex items-center gap-2 py-2">
                    <input
                        type="checkbox"
                        id="wizard-remember"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        data-testid="wizard-remember-checkbox"
                        className="h-4 w-4"
                    />
                    <Label htmlFor="wizard-remember" className="text-sm text-muted-foreground cursor-pointer">
                        {t('surveys.preflightRemember')}
                    </Label>
                </div>
                <DialogFooter className="flex flex-row justify-between gap-2">
                    <Button variant="outline" onClick={() => onSkip(remember)} data-testid="preflight-skip-btn">
                        {t('common.skip')}
                    </Button>
                    <Button onClick={onGuided} data-testid="preflight-guided-btn">
                        {t('surveys.startGuidedSetup')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SurveysList;